/**
 * SeatSnap Main Entry Point
 *
 * Single bundle entry point that conditionally initializes modules based on DOM presence.
 * Eliminates HTMX duplication and provides better caching across page navigations.
 */

import Alpine from 'alpinejs';
import htmx from 'htmx.org';
import { guestManagerStore } from './guest-manager/store.js';

// Expose HTMX globally for HTML attributes
window.htmx = htmx;

// Initialize Alpine.js
window.Alpine = Alpine;

// Register Alpine stores before starting
Alpine.store('guestManager', guestManagerStore);

// Start Alpine (must be called before DOM loads for x-cloak to work)
Alpine.start();

/**
 * Initialize modules based on what's present in the DOM
 */
async function initializeModules(): Promise<void> {
  const loadingPulser = document.getElementById('app-loading');

  try {
    // Canvas module - check for #canvas-root
    const canvasRoot = document.getElementById('canvas-root');
    if (canvasRoot) {
      const { mountCanvas } = await import('./canvas/index.js');
      // mountCanvas is called automatically by canvas/index.ts autoMount
      // We just need to ensure the module is loaded
    }

    // Guest Manager module - check for [data-guest-manager]
    const guestManager = document.querySelector('[data-guest-manager]');
    if (guestManager) {
      await import('./guest-manager/index.js');
      // Guest Manager module auto-initializes on DOMContentLoaded
    }
  } catch (error) {
    console.error('Failed to initialize modules:', error);
  } finally {
    // Hide loading state with fade-out transition
    if (loadingPulser) {
      loadingPulser.style.opacity = '0';
      setTimeout(() => {
        loadingPulser.remove();
      }, 300);
    }
  }
}

// Wait for DOM ready before initializing
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeModules);
} else {
  // DOM already loaded (e.g., script loaded after page)
  initializeModules();
}
